import { useCallback, useState } from 'react';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { PopperMenu, PopperMenuItem, ParentPopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { MoreVerticalAnchorSmall } from '@/components/presentation/Anchor';
import { cx } from '@trinity/chat-components';
import type { ContextMenuItem, ContextMenuProps } from './interfaces';
import styles from './style.module.css';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';

export const ContextMenu = ({ className, AnchorElement = MoreVerticalAnchorSmall, items, options = {} }: ContextMenuProps) => {
  const [open, setOpen] = useState(false);
  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      zIndexMiddleware,
    ],
    placement: options.placement ?? 'right-start',
  });

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <>
      <div
        className={cx(styles.anchor, className)}
        onClick={() => setOpen(!open)}
        ref={refs.setReference}>
        <AnchorElement />
      </div>
      {open && (
        <Popper
          open={open}
          ref={refs.setFloating}
          style={floatingStyles}
          anchorEl={context.elements.domReference}
          placement={options.placement ?? 'right-start'}>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            touchEvent='onTouchStart'
            onClickAway={closeMenu}>
            <PopperMenu>
              {items.filter(i => !i.hidden).map((item, index) => (
                <MenuItem
                  key={index}
                  item={item}
                  onClose={closeMenu} />
              ))}
            </PopperMenu>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

type MenuItemProps = {
  item: ContextMenuItem;
  onClose: () => void;
};

const MenuItem = ({ item, ...props }: MenuItemProps) => {
  if (item.children?.length) {
    return <ParentMenuItem item={item} {...props} />;
  } else {
    return (
      <PopperMenuItem
        className={styles.menuItem}
        disabled={item.disabled}
        onClick={() => {
          item.onClick();
          props.onClose();
        }}>
        {typeof item.label === 'string' ? item.label : <item.label />}
      </PopperMenuItem>
    );
  }
};

const ParentMenuItem = ({ item, ...props }: MenuItemProps) => {
  const [open, setOpen] = useState(false);
  const zIndexMiddleware = useZIndexMiddleware();

  const { refs, floatingStyles } = useFloating({
    middleware: [zIndexMiddleware],
    placement: 'right',
  });

  return (
    <>
      <ParentPopperMenuItem
        ref={refs.setReference}
        disabled={item.disabled}
        onClick={() => setOpen(true)}>
        {typeof item.label === 'string' ? item.label : <item.label />}
      </ParentPopperMenuItem>
      {open && (
        <ClickAwayListener
          onClickAway={() => setOpen(false)}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}>
            <PopperMenu>
              {item.children.map((child, index) => (
                <MenuItem
                  key={index}
                  item={child}
                  {...props} />
              ))}
            </PopperMenu>
          </div>
        </ClickAwayListener>
      )}
    </>
  );
};